import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WithDynamicContent } from '@woolworthsnz/dynamic-content';
import {
	RecipeBreadcrumbService,
	GridComponent,
	SortHeaderComponent,
	ResultsHeaderComponent,
	BreadcrumbComponent,
} from '@woolworthsnz/recipe';
import { InfiniteScrollingService, SearchMediatorService } from '@woolworthsnz/search';
import { NOT_FOUND_PATH } from '@woolworthsnz/shop';
import {
	AppSettingsService,
	DatalayerService,
	ModalOverlayService,
	parseStringToIntOrDefault,
	WithPageMeta,
	LoadMoreComponent,
	EmbeddedVisibilityDirective,
	WappleLoadingComponent,
} from '@woolworthsnz/styleguide';
import { EMPTY, Subject } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { RecipePageType, RecipesStoreService } from '../../services/recipes-store.service';
import { RecipesResultsBasePage } from '../recipes-results-base/recipes-results-base.page';
import { NgIf, AsyncPipe } from '@angular/common';

@WithPageMeta({ pageTitle: 'recipeCategory' })
@WithDynamicContent()
@Component({
	template: `
		<ng-container *ngIf="state$ | async; let state">
			<cdx-wapple-loading *ngIf="state.loading; else content"></cdx-wapple-loading>
			<ng-template #content>
				<header>
					<recipe-breadcrumb *cdxEmbedded="'hide'"></recipe-breadcrumb>
					<recipe-results-header
						*ngIf="isResultHeaderVisible"
						[id]="state.category.id"
						[title]="state.category.displayName || state.category.description"
						[description]="state.category.detail"
						[subCategories]="state.category.subcategories"
						[totalRecipes]="state.totalRecipes"
					></recipe-results-header>
					<recipe-sort-header
						*ngIf="state.totalRecipes > 0"
						[sortOptions]="state.sortOptions"
						[currentSortOption]="state.currentSortOption"
						(sortChange)="sortChange($event)"
					></recipe-sort-header>
				</header>
				<div class="recipeItems">
					<recipe-grid
						[savedRecipeIds]="state.savedRecipes"
						[recipeStamps]="mapToStamps(state.visibleRecipes)"
						(saveRecipe)="handleSave($event)"
						(columnCountChange)="handleColumnCountChange($event)"
					></recipe-grid>
				</div>
				<footer>
					<cdx-load-more
						[currentlyShowing]="state.visibleRecipes.length"
						[totalItems]="state.totalRecipes"
						[loading]="state.newPageLoading"
						(loadMore)="loadMore()"
					>
						<span i18n="@@recipes-loadMoreButton">Load more deliciousness</span>
					</cdx-load-more>
				</footer>
			</ng-template>
			<div #scrollAnchor></div>
		</ng-container>
	`,
	styleUrls: ['../recipes-results-base/recipes-results-base.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		WappleLoadingComponent,
		EmbeddedVisibilityDirective,
		BreadcrumbComponent,
		ResultsHeaderComponent,
		SortHeaderComponent,
		GridComponent,
		LoadMoreComponent,
		AsyncPipe,
	],
})
export class RecipesCategoryPage extends RecipesResultsBasePage implements OnInit, OnDestroy {
	@ViewChild('scrollAnchor') scrollAnchor: ElementRef<HTMLElement>;
	isParentCategory: boolean;
	private destroy$: Subject<boolean> = new Subject();

	constructor(
		protected recipesStore: RecipesStoreService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected modalOverlayService: ModalOverlayService,
		protected searchMediatorService: SearchMediatorService,
		protected infiniteScrollingService: InfiniteScrollingService,
		protected datalayerService: DatalayerService,
		protected appSettingsService: AppSettingsService,
		protected recipeBreadcrumbService: RecipeBreadcrumbService
	) {
		super(
			recipesStore,
			route,
			router,
			modalOverlayService,
			searchMediatorService,
			infiniteScrollingService,
			datalayerService,
			RecipePageType.category,
			recipeBreadcrumbService
		);
	}

	ngOnInit(): void {
		this.initialize();
		this.route.paramMap
			.pipe(
				map((routeParams) => {
					this.isParentCategory = routeParams.get('subcategoryId') === null;
					return routeParams.get('subcategoryId') || routeParams.get('categoryId');
				}),
				mergeMap((categoryIdFromRoute) => {
					const categoryId = parseStringToIntOrDefault(categoryIdFromRoute, 0);
					return this.recipesStore
						.loadCategoryRecipes(categoryId, this.sortOption)
						.pipe(this.takeUntilRouteChange());
				}),
				takeUntil(this.destroy$),
				catchError(() => {
					this.router.navigate([NOT_FOUND_PATH], { skipLocationChange: true });
					return EMPTY;
				}),
				tap(() => this.setupScroller(this.scrollAnchor))
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
		this.disconnect();
	}

	get isResultHeaderVisible(): boolean {
		// always show header when not in embedded view
		if (!this.appSettingsService.getSetting('isEmbeddedInApp')) {
			return true;
		}

		// when in embedded view, only show result header in parent category page
		return this.isParentCategory;
	}
}
